import { useState, useCallback } from 'react';
import { api } from '../api/api';
import toast from 'react-hot-toast';

const STATUS_MAP = {
  none: { label: '미발송', color: 'default' },
  sent: { label: '발송됨', color: 'info' },
  signed: { label: '서명 완료', color: 'success' },
  expired: { label: '만료', color: 'error' },
};

/** 계약서 Client는 점주 이름만 사용 — 매장명(store.name)은 넣지 않음 */
export const buildDefaultClientLine = (store, ownerNameInput) => {
  const cityLine = [store?.town, store?.state, store?.zipcode].filter(Boolean).join(', ');
  const addr = [store?.address1, cityLine].filter(Boolean).join(', ');
  const name = (ownerNameInput ?? store?.ownerName ?? '').trim();
  if (!name && !addr) return '';
  if (!name) return `${addr} ("Client")`;
  return addr ? `${name}, ${addr} ("Client")` : `${name} ("Client")`;
};

export { STATUS_MAP };

export function useStoreContract({ onStoreChange } = {}) {
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDialogLoading, setSendDialogLoading] = useState(false);
  const [sendStore, setSendStore] = useState(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sendOwnerPhone, setSendOwnerPhone] = useState('');
  const [sendClientName, setSendClientName] = useState('');
  const [sendStoreName, setSendStoreName] = useState('');
  const [sendClientLine, setSendClientLine] = useState('');
  const [sendContractDate, setSendContractDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [uploadStore, setUploadStore] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  const [editStore, setEditStore] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editSignee, setEditSignee] = useState('');

  const [logStore, setLogStore] = useState(null);
  const [logs, setLogs] = useState([]);

  const refreshStore = useCallback(async (storeId) => {
    try {
      const { data } = await api.get(`/store/get/${storeId}`);
      const latest = data?.store || data;
      if (latest && onStoreChange) onStoreChange(latest);
      return latest;
    } catch (_) {
      return null;
    }
  }, [onStoreChange]);

  const closeSendDialog = () => {
    setSendDialogOpen(false);
    setSendStore(null);
  };

  const openSendDialog = async (store) => {
    setSendDialogOpen(true);
    setSendDialogLoading(true);
    const baseOwnerName = (store.ownerName || '').trim();
    setSendStore(store);
    setSendEmail(store.ownerEmail || '');
    setSendOwnerPhone(store.ownerPhone || '');
    setSendClientName(baseOwnerName);
    setSendStoreName(store.name || store.tljNo || '');
    setSendClientLine(buildDefaultClientLine(store, baseOwnerName));
    setSendContractDate(new Date().toISOString().slice(0, 10));
    const latest = await refreshStore(store._id);
    if (latest) {
      const ownerOnly = (latest.ownerName || '').trim();
      setSendStore(latest);
      setSendEmail(latest.ownerEmail || '');
      setSendOwnerPhone(latest.ownerPhone || '');
      setSendClientName(ownerOnly);
      setSendStoreName(latest.name || latest.tljNo || '');
      setSendClientLine(buildDefaultClientLine(latest, ownerOnly));
    }
    setSendDialogLoading(false);
  };

  const generateContractOnly = async () => {
    if (!sendStore) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/store/generate-contract/${sendStore._id}`, {
        clientName: sendClientName,
        storeName: sendStoreName,
        clientLine: sendClientLine,
        email: sendEmail,
        ownerPhone: sendOwnerPhone,
        contractDate: sendContractDate,
      });
      toast.success(
        data?.contactUpdated?.length
          ? `계약서 생성 완료 · 스토어에 ${data.contactUpdated.join(', ')} 저장됨`
          : '계약서 생성 완료',
      );
      if (data.pdfUrl) window.open(data.pdfUrl, '_blank');
      const latest = await refreshStore(sendStore._id);
      if (latest) setSendStore(latest);
    } catch (e) {
      toast.error('생성 실패: ' + (e.response?.data?.message || e.message));
    }
    setGenerating(false);
  };

  const doSend = async () => {
    if (!sendStore || !sendEmail) return;
    setSending(true);
    try {
      const { data } = await api.post(`/store/contract-send/${sendStore._id}`, {
        email: sendEmail,
        ownerPhone: sendOwnerPhone,
        clientName: sendClientName,
        storeName: sendStoreName,
        clientLine: sendClientLine,
        contractDate: sendContractDate,
      });
      toast.success(
        data?.contactUpdated?.length
          ? `계약서 발송 완료: ${sendEmail} · 스토어 연락처 저장됨`
          : `계약서 발송 완료: ${sendEmail}`,
      );
      closeSendDialog();
      await refreshStore(sendStore._id);
    } catch (e) {
      toast.error('발송 실패: ' + (e.response?.data?.message || e.message));
    }
    setSending(false);
  };

  const openUploadDialog = (store) => {
    setUploadStore(store);
    setUploadFile(null);
  };

  const doUpload = async () => {
    if (!uploadStore || !uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('contract', uploadFile);
      const { data } = await api.post(`/store/contract-upload/${uploadStore._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('업로드 완료: ' + data.fileName);
      setUploadStore(null);
      await refreshStore(uploadStore._id);
    } catch (e) {
      toast.error('업로드 실패: ' + (e.response?.data?.message || e.message));
    }
    setUploading(false);
  };

  const openEditDialog = (store) => {
    setEditStore(store);
    setEditStatus(store.contractStatus || 'none');
    setEditNote('');
    setEditSignee(store.contractSigneeName || store.ownerName || '');
  };

  const saveStatus = async () => {
    if (!editStore) return;
    setSavingStatus(true);
    try {
      await api.patch(`/store/contract-status/${editStore._id}`, {
        status: editStatus,
        note: editNote,
        signeeName: editSignee,
      });
      toast.success('상태 변경 완료');
      setEditStore(null);
      await refreshStore(editStore._id);
    } catch (e) {
      toast.error('변경 실패: ' + (e.response?.data?.message || e.message));
    }
    setSavingStatus(false);
  };

  const openLogs = async (store) => {
    setLogStore(store);
    try {
      const { data } = await api.get(`/store/contract-logs/${store._id}`);
      setLogs(data.logs || []);
    } catch (_) {
      setLogs([]);
    }
  };

  return {
    generating,
    sending,
    uploading,
    savingStatus,
    sendDialogOpen,
    sendDialogLoading,
    sendStore,
    sendEmail,
    setSendEmail,
    sendOwnerPhone,
    setSendOwnerPhone,
    sendClientName,
    setSendClientName,
    sendStoreName,
    setSendStoreName,
    sendClientLine,
    setSendClientLine,
    sendContractDate,
    setSendContractDate,
    uploadStore,
    uploadFile,
    setUploadFile,
    editStore,
    setEditStore,
    editStatus,
    setEditStatus,
    editNote,
    setEditNote,
    editSignee,
    setEditSignee,
    logStore,
    setLogStore,
    logs,
    setUploadStore,
    closeSendDialog,
    openSendDialog,
    generateContractOnly,
    doSend,
    openUploadDialog,
    doUpload,
    openEditDialog,
    saveStatus,
    openLogs,
  };
}
